import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import KeralitOptionsPanel from '../components/KeralitOptionsPanel';
import KeralitDrawingBoard from '../components/KeralitDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  keralitProfileSystems,
  glassTextures, 
  glazingTypes, 
  colors, 
  woodJointOptions,
  PRICING,
  normalizeKeralitProfileSystem,
} from '../data/mockData';
import {
  applyPersistedFrameColors,
  trackFrameColorChange,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const DEFAULT_KERALIT_CONFIG = {
  width: 1500,
  height: 1500,
  profileSystem: 'potdeksel-190',
  interiorColor: 'ral-7016',
  exteriorColor: 'anthracite',
  lineType: 'horizontal',
  lineSpacing: '19cm',
  quantity: 1
};

const Keralit = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    withDefaultAdditionalItems({
      ...applyPersistedFrameColors(DEFAULT_KERALIT_CONFIG, DOOR_COLOR_FIELDS),
      profileSystem: normalizeKeralitProfileSystem(DEFAULT_KERALIT_CONFIG.profileSystem),
    });

  const { config, setConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) =>
      withDefaultAdditionalItems({
        ...applyPersistedFrameColors(loaded, DOOR_COLOR_FIELDS),
        profileSystem: normalizeKeralitProfileSystem(loaded?.profileSystem),
      })
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, DOOR_COLOR_FIELDS);
    markDirty();
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <ConfiguratorShell
      title={t('configurator.keralitConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <div className="h-full overflow-y-auto p-4 bg-white border-r border-gray-200">
          <KeralitOptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
            additionalItems={config.additionalItems || createDefaultAdditionalItems()}
            onAdditionalItemsChange={handleAdditionalItemsChange}
          />
        </div>
      }
      previewPanel={
        <KeralitDrawingBoard
          width={config.width}
          height={config.height}
          onDimensionsChange={(newWidth, newHeight) => {
            markDirty();
            setConfig((prev) => ({ ...prev, width: newWidth, height: newHeight }));
          }}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={keralitProfileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={[]}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="keralit"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={markClean}
        />
      }
    />
  );
};

export default Keralit;

